"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getPalette } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";

export type BarRow<M = unknown> = {
  label: string;
  value: number;
  color: string;
  /** Caller-supplied row metadata, available to tooltip + label formatters. */
  meta?: M;
};

export type LegendEntry = { label: string; color: string };

type Props<M = unknown> = {
  data: BarRow<M>[];
  height: number;
  valueDomain?: [number, number];
  xTickFormatter?: (v: number) => string;
  valueLabelFormatter: (v: number, row: BarRow<M>) => string;
  /** Right-hand axis label for the tooltip — string, or a function of the row. */
  tooltipLabel: string | ((row: BarRow<M>) => string);
  tooltipValueFormatter?: (v: number, row: BarRow<M>) => string;
  referenceLineAt?: number;
  categoryWidth?: number;
  legend?: LegendEntry[];
  sort?: "asc" | "desc" | "none";
};

export function HorizontalCategoricalBar<M = unknown>({
  data,
  height,
  valueDomain,
  xTickFormatter,
  valueLabelFormatter,
  tooltipLabel,
  tooltipValueFormatter,
  referenceLineAt,
  categoryWidth = 260,
  legend,
  sort = "desc",
}: Props<M>) {
  const p = getPalette(usePrefersDark());
  const rows =
    sort === "none"
      ? data
      : [...data].sort((a, b) =>
          sort === "desc" ? b.value - a.value : a.value - b.value,
        );

  const formatTooltipValue = tooltipValueFormatter ?? valueLabelFormatter;

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 8, right: 80, bottom: 8, left: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--rule)" horizontal={false} />
          <XAxis
            type="number"
            domain={valueDomain}
            tickFormatter={xTickFormatter}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={categoryWidth}
            fontSize={12}
            interval={0}
          />
          {referenceLineAt !== undefined && (
            <ReferenceLine x={referenceLineAt} stroke={p.ink} strokeWidth={1} />
          )}
          <Tooltip
            formatter={(v, _n, item) => {
              const row = item.payload as BarRow<M>;
              const label =
                typeof tooltipLabel === "function" ? tooltipLabel(row) : tooltipLabel;
              return [formatTooltipValue(Number(v), row), label];
            }}
            labelFormatter={(label) => `${label}`}
          />
          <Bar dataKey="value">
            {rows.map((row, i) => (
              <Cell key={`${i}-${row.label}`} fill={row.color} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              content={({ x, y, width, height: h, value, index }) => {
                if (typeof index !== "number") return null;
                const row = rows[index];
                if (!row) return null;
                const text = valueLabelFormatter(Number(value), row);
                return (
                  <text
                    x={Number(x) + Number(width) + 6}
                    y={Number(y) + Number(h) / 2}
                    fontSize={11}
                    dominantBaseline="middle"
                    fill={p.ink}
                  >
                    {text}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {legend && legend.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          {legend.map((entry, i) => (
            <span
              key={`${i}-${entry.color}-${entry.label}`}
              className="flex items-center gap-2"
            >
              <span
                aria-hidden
                className="inline-block h-3 w-3"
                style={{ background: entry.color }}
              />
              <span>{entry.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
